import { useState, useEffect, useCallback } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Modal, Button, Form, Pagination } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import { toast } from 'react-toastify';
import './VideoContributionsList.css';
import { extractObjectId, formatDate, formatDateTime } from '../../../utils/utils';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { FaVideo, FaCheck, FaTimes, FaEye, FaTrash, FaYoutube, FaExternalLinkAlt } from 'react-icons/fa';

/**
 * Extract YouTube video ID from URL
 */
function extractYouTubeVideoId(url) {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
    }
    return null;
}

function getYouTubeThumbnail(url) {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
}

const VideoContributionsList = () => {
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedContribution, setSelectedContribution] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const itemsPerPage = 10;

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkRejectionReason, setBulkRejectionReason] = useState('');
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const fetchContributions = useCallback(async (page = 1, status = '') => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: itemsPerPage,
            };
            if (status) {
                params.status = status;
            }
            const response = await allAPiServicesCall.getAllVideoContributions(params, getAuthConfig());
            if (response?.status === 200) {
                setContributions(response.data || []);
                if (response.pagination) {
                    setCurrentPage(response.pagination.currentPage);
                    setTotalPages(response.pagination.totalPages);
                    setTotalCount(response.pagination.totalCount);
                }
            } else {
                setContributions([]);
            }
        } catch (error) {
            console.error("Error fetching contributions:", error);
            toast.error('Error fetching contributions');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchContributionDetails = async (id) => {
        const contributionId = extractObjectId(id);
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getVideoContributionById(getAuthConfig(), contributionId);
            if (response?.data) {
                setSelectedContribution(response.data);
                setModalOpen(true);
            }
        } catch (error) {
            toast.error('Error fetching contribution details');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveOrReject = async (id, status) => {
        if (status === 'rejected' && !rejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionId: extractObjectId(id),
                status: status,
                ...(status === 'rejected' && { rejectionReason: rejectionReason.trim() }),
            };
            await allAPiServicesCall.processVideoContribution(payload, getAuthConfig());

            setModalOpen(false);
            setRejectionReason('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setProcessing(false);
        }
    };

    const openDeleteModal = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteTargetId) return;

        setProcessing(true);
        try {
            const contributionId = extractObjectId(deleteTargetId);
            await allAPiServicesCall.deleteVideoContribution({ id: contributionId }, getAuthConfig());
            setShowDeleteModal(false);
            setDeleteTargetId(null);
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error('Error deleting contribution');
        } finally {
            setProcessing(false);
        }
    };

    // Bulk selection handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pendingIds = contributions
                .filter(c => c.status === 'pending')
                .map(c => extractObjectId(c._id));
            setSelectedIds(pendingIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        const extractedId = extractObjectId(id);
        setSelectedIds(prev => {
            if (prev.includes(extractedId)) {
                return prev.filter(i => i !== extractedId);
            } else {
                return [...prev, extractedId];
            }
        });
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one contribution');
            return;
        }

        if (bulkAction === 'rejected' && !bulkRejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionIds: selectedIds,
                status: bulkAction,
                ...(bulkAction === 'rejected' && { rejectionReason: bulkRejectionReason.trim() }),
            };
            await allAPiServicesCall.bulkProcessVideoContributions(payload, getAuthConfig());

            setShowBulkModal(false);
            setBulkRejectionReason('');
            setSelectedIds([]);
            setBulkAction('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message || 'Error processing bulk action');
        } finally {
            setProcessing(false);
        }
    };

    const openBulkModal = (action) => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one pending contribution');
            return;
        }
        setBulkAction(action);
        setShowBulkModal(true);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            pending: { backgroundColor: '#ffc107', color: '#000' },
            approved: { backgroundColor: '#28a745', color: '#fff' },
            rejected: { backgroundColor: '#dc3545', color: '#fff' },
        };
        return (
            <span
                className="badge px-2 py-1 rounded"
                style={statusStyles[status] || statusStyles.pending}
            >
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedIds([]);
        fetchContributions(page, statusFilter);
    };

    const handleStatusFilterChange = (e) => {
        const newStatus = e.target.value;
        setStatusFilter(newStatus);
        setCurrentPage(1);
        setSelectedIds([]);
        fetchContributions(1, newStatus);
    };

    // Generate pagination items
    const renderPagination = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={currentPage === 1} />,
            <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        );

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />,
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        );

        return items;
    };

    useEffect(() => {
        fetchContributions(1, '');
    }, [fetchContributions]);

    const pendingCount = contributions.filter(c => c.status === 'pending').length;

    return (
        <CommonLayout>
            <div className="vc-container">
                {/* Premium Header */}
                <div className="vc-header">
                    <div className="vc-header-top">
                        <div className="vc-title-wrapper">
                            <div className="vc-title-icon">
                                <FaYoutube />
                            </div>
                            <div className="vc-title-text">
                                <h1 className="vc-title">Video Contributions</h1>
                                <p className="vc-subtitle">Review and approve YouTube video links submitted by users</p>
                            </div>
                        </div>
                        <div className="vc-stats">
                            <div className="vc-stat-badge">
                                <FaVideo />
                                <span>Total: <strong>{totalCount}</strong></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter and Bulk Actions Bar */}
                <div className="vc-filter-section">
                    <div className="vc-filter-left">
                        <select
                            className="vc-filter-select"
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <div className="vc-filter-info">
                            <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                        </div>
                    </div>

                    {/* Bulk Action Buttons */}
                    <div className="vc-filter-right">
                        <button
                            className="vc-bulk-btn vc-bulk-btn-approve"
                            onClick={() => openBulkModal('approved')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaCheck /> Bulk Approve ({selectedIds.length})
                        </button>
                        <button
                            className="vc-bulk-btn vc-bulk-btn-reject"
                            onClick={() => openBulkModal('rejected')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaTimes /> Bulk Reject ({selectedIds.length})
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="vc-table-card">
                        <div className="vc-loading">
                            <div className="vc-loading-spinner"></div>
                            <p className="vc-loading-text">Loading contributions...</p>
                        </div>
                    </div>
                ) : contributions.length > 0 ? (
                    <div className="vc-table-card">
                        <div className="vc-table-container">
                            <table className="vc-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                className="vc-checkbox"
                                                onChange={handleSelectAll}
                                                checked={pendingCount > 0 && selectedIds.length === pendingCount}
                                                disabled={pendingCount === 0}
                                                title="Select all pending contributions"
                                            />
                                        </th>
                                        <th>Song</th>
                                        <th>Artist</th>
                                        <th>Video Preview</th>
                                        <th>Contributed By</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contributions.map((contribution) => (
                                        <tr key={contribution._id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="vc-checkbox"
                                                    checked={selectedIds.includes(extractObjectId(contribution._id))}
                                                    onChange={() => handleSelectOne(contribution._id)}
                                                    disabled={contribution.status !== 'pending'}
                                                />
                                            </td>
                                            <td><span className="vc-song-name">{contribution.songName || 'Unknown Song'}</span></td>
                                            <td><span className="vc-artist-name">{contribution.artistName || 'Unknown Artist'}</span></td>
                                            <td>
                                                <div className="vc-thumbnail-wrapper">
                                                    {getYouTubeThumbnail(contribution.youtubeUrl) ? (
                                                        <img
                                                            src={getYouTubeThumbnail(contribution.youtubeUrl)}
                                                            alt="Video thumbnail"
                                                            className="vc-thumbnail"
                                                        />
                                                    ) : (
                                                        <div className="vc-no-thumbnail">
                                                            <FaVideo />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td><span className="vc-contributor">{contribution.user_data?.username || 'N/A'}</span></td>
                                            <td>
                                                <span className={`vc-status-badge ${contribution.status}`}>
                                                    {contribution.status?.charAt(0).toUpperCase() + contribution.status?.slice(1)}
                                                </span>
                                            </td>
                                            <td><span className="vc-date">{formatDate(contribution.createdAt)}</span></td>
                                            <td>
                                                <div className="vc-actions">
                                                    <button
                                                        className="vc-action-btn vc-action-btn-view"
                                                        onClick={() => fetchContributionDetails(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaEye /> View
                                                    </button>
                                                    <button
                                                        className="vc-action-btn vc-action-btn-delete"
                                                        onClick={() => openDeleteModal(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="vc-pagination-wrapper">
                                <Pagination>{renderPagination()}</Pagination>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="vc-table-card">
                        <div className="vc-empty">
                            <div className="vc-empty-icon">
                                <FaYoutube />
                            </div>
                            <p className="vc-empty-text">No video contributions found.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {modalOpen && selectedContribution && (
                <Modal show={modalOpen} onHide={() => { setModalOpen(false); setRejectionReason(''); }} size="lg" centered className="vc-detail-modal">
                    <Modal.Header className="vc-detail-header">
                        <div className="vc-detail-header-content">
                            <div className="vc-detail-icon">
                                <FaYoutube size={28} />
                            </div>
                            <div>
                                <Modal.Title className="vc-detail-title">
                                    {selectedContribution.songName || 'Video Contribution'}
                                </Modal.Title>
                                <p className="vc-detail-subtitle">
                                    by {selectedContribution.artistName || 'Unknown Artist'}
                                </p>
                            </div>
                        </div>
                        <button className="vc-detail-close" onClick={() => { setModalOpen(false); setRejectionReason(''); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </Modal.Header>
                    <Modal.Body className="vc-detail-body">
                        {/* Info Cards */}
                        <div className="vc-info-grid">
                            <div className="vc-info-card">
                                <span className="vc-info-label">Status</span>
                                <span className="vc-info-value">{getStatusBadge(selectedContribution.status)}</span>
                            </div>
                            <div className="vc-info-card">
                                <span className="vc-info-label">Contributed By</span>
                                <span className="vc-info-value">{selectedContribution.user_data?.username || 'Unknown'}</span>
                                <span className="vc-info-email">{selectedContribution.user_data?.email || ''}</span>
                            </div>
                            <div className="vc-info-card">
                                <span className="vc-info-label">Submitted</span>
                                <span className="vc-info-value">{formatDate(selectedContribution.createdAt)}</span>
                                <span className="vc-info-email">{formatDateTime(selectedContribution.createdAt, '').split(', ')[1] || ''}</span>
                            </div>
                        </div>

                        {/* Video Comparison */}
                        <div className="vc-video-comparison">
                            {/* Current Video (if exists) */}
                            {selectedContribution.currentVideoUrl && (
                                <div className="vc-video-section">
                                    <div className="vc-section-header">
                                        <span className="vc-section-icon current">
                                            <FaVideo />
                                        </span>
                                        <span className="vc-section-title">Current Video</span>
                                    </div>
                                    <div className="vc-video-preview">
                                        {getYouTubeThumbnail(selectedContribution.currentVideoUrl) ? (
                                            <img
                                                src={getYouTubeThumbnail(selectedContribution.currentVideoUrl)}
                                                alt="Current video thumbnail"
                                                className="vc-preview-thumbnail"
                                            />
                                        ) : (
                                            <div className="vc-no-video">No video available</div>
                                        )}
                                        <a
                                            href={selectedContribution.currentVideoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="vc-video-link"
                                        >
                                            <FaExternalLinkAlt /> Open in YouTube
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* New Video */}
                            <div className="vc-video-section">
                                <div className="vc-section-header">
                                    <span className="vc-section-icon new">
                                        <FaYoutube />
                                    </span>
                                    <span className="vc-section-title">
                                        {selectedContribution.currentVideoUrl ? 'Proposed Video' : 'New Video'}
                                    </span>
                                </div>
                                <div className="vc-video-preview">
                                    {getYouTubeThumbnail(selectedContribution.youtubeUrl) ? (
                                        <img
                                            src={getYouTubeThumbnail(selectedContribution.youtubeUrl)}
                                            alt="Proposed video thumbnail"
                                            className="vc-preview-thumbnail"
                                        />
                                    ) : (
                                        <div className="vc-no-video">Invalid video URL</div>
                                    )}
                                    <a
                                        href={selectedContribution.youtubeUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="vc-video-link"
                                    >
                                        <FaExternalLinkAlt /> Open in YouTube
                                    </a>
                                </div>
                                <div className="vc-url-display">
                                    <code>{selectedContribution.youtubeUrl}</code>
                                </div>
                            </div>
                        </div>

                        {/* Rejection Reason (if rejected) */}
                        {selectedContribution.status === 'rejected' && selectedContribution.rejectionReason && (
                            <div className="vc-section">
                                <div className="vc-section-header">
                                    <span className="vc-section-icon rejected">
                                        <FaTimes />
                                    </span>
                                    <span className="vc-section-title">Rejection Reason</span>
                                </div>
                                <div className="vc-rejection-box">
                                    {selectedContribution.rejectionReason}
                                </div>
                            </div>
                        )}

                        {/* Rejection Input (if pending) */}
                        {selectedContribution.status === 'pending' && (
                            <div className="vc-section">
                                <div className="vc-section-header">
                                    <span className="vc-section-icon rejected">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                    </span>
                                    <span className="vc-section-title">Rejection Reason (if rejecting)</span>
                                </div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter reason for rejection..."
                                    className="vc-rejection-input"
                                />
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="vc-detail-footer">
                        {selectedContribution.status === 'pending' && (
                            <>
                                <Button
                                    className="vc-action-btn-modal approve"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'approved')}
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck className="me-2" />
                                            Approve & Update Video
                                        </>
                                    )}
                                </Button>
                                <Button
                                    className="vc-action-btn-modal reject"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'rejected')}
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : (
                                        <>
                                            <FaTimes className="me-2" />
                                            Reject
                                        </>
                                    )}
                                </Button>
                            </>
                        )}
                        <Button
                            className="vc-action-btn-modal close-btn"
                            onClick={() => { setModalOpen(false); setRejectionReason(''); }}
                            disabled={processing}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Bulk Action Modal */}
            <ConfirmationModal
                show={showBulkModal}
                handleClose={() => { setShowBulkModal(false); setBulkRejectionReason(''); }}
                onConfirm={handleBulkAction}
                title={`Bulk ${bulkAction === 'approved' ? 'Approve' : 'Reject'} Video Contributions`}
                message={`You are about to ${bulkAction === 'approved' ? 'approve' : 'reject'} ${selectedIds.length} video contribution(s).${bulkAction === 'approved' ? ' Song videos will be updated immediately.' : ''}`}
                confirmText={`Confirm ${bulkAction === 'approved' ? 'Approve' : 'Reject'}`}
                cancelText="Cancel"
                variant={bulkAction === 'approved' ? 'success' : 'danger'}
                isLoading={processing}
                confirmDisabled={bulkAction === 'rejected' && !bulkRejectionReason.trim()}
            >
                {bulkAction === 'rejected' && (
                    <Form.Group>
                        <Form.Label><strong>Rejection Reason (required):</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={bulkRejectionReason}
                            onChange={(e) => setBulkRejectionReason(e.target.value)}
                            placeholder="Enter reason for bulk rejection..."
                        />
                    </Form.Group>
                )}
            </ConfirmationModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
                onConfirm={handleDelete}
                title="Delete Video Contribution"
                message="Are you sure you want to delete this video contribution? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={processing}
            />
        </CommonLayout>
    );
};

export default VideoContributionsList;
